/**
 * 매칭리스트 문자발송 — 탭별 테이블 렌더러 (내부매칭 / 외부배차 / 수주응답)
 *
 * 발송이력은 메인 클래스에 그대로 두고 (펼치기 row.child 가 historyDt 인스턴스에 의존),
 * 여기서는 #tA 컨테이너에 그리는 3개 테이블만 담당.
 *
 * parent (MatchList 인스턴스) 에서 사용:
 *   state, internalMembers, externalWithDistance, responseList, ORDER
 *   externalListFiltered(), externalListSendable()
 */
import {
    initDataTable, daysSince, lastAccessSortKey, distanceColor, LEVEL_COLORS, isRestricted, msgTypeBadge,
} from './MatchListUtils';
import Util from '../../../Common/Util';

export default class MatchListTables {
    constructor(parent) {
        this.parent = parent;

        // 외부회원 편집 버튼(.om-edit-btn) — 내부/외부 테이블 모두 #tA 에 렌더되므로 위임 1회 바인딩.
        $('#tA').off('click.omedit').on('click.omedit', '.om-edit-btn', (ev) => {
            ev.stopPropagation();
            const id = +$(ev.currentTarget).data('id');
            if (!id) return;
            $j.F.Admin.OutMember.EditForm.open(id, { skipSuccessToast: true, onSaved: () => this._onOutMemberSaved() });
        });
    }

    // ============================================================
    //  내부매칭 테이블 — sendMode 시 체크박스 컬럼 추가, 차수별 색상
    // ============================================================
    renderInternal() {
        const p = this.parent;
        const { state, internalMembers } = p;
        // 최종접속 정렬 — '': 원본순, 'oldest': 오래된순(높은 daysSince 먼저), 'newest': 최신순
        const dir = state.sortByInactive;
        const sorted = dir === 'oldest'
            ? [...internalMembers].sort((a, b) => daysSince(b.lastAccess) - daysSince(a.lastAccess))
            : dir === 'newest'
                ? [...internalMembers].sort((a, b) => daysSince(a.lastAccess) - daysSince(b.lastAccess))
                : internalMembers;
        // 전체 선택 표시 — 수발주제한 회원은 일괄 자동선택 대상이 아니므로 sendable 기준
        const sendable = internalMembers.filter(m => !isRestricted(m));
        const allChecked = sendable.length > 0 && sendable.every(m => state.checkedInternal.has(m.no));
        const sortArrow  = dir === 'oldest' ? '▼' : (dir === 'newest' ? '▲' : '▽');

        // sendMode/탭 마커 동일하면 기존 DataTable 재사용 → 헤더정렬·페이지 자동 보존.
        // clear+add+draw 로 셀이 강제 재렌더되어 체크박스 HTML 도 최신 state 반영.
        // sendMode 토글 시에는 컬럼 수가 바뀌므로 마커 미스매치 → 아래 rebuild 경로로 빠짐.
        // 외부배차 오더: 지정 배차자(외부 1명)를 매칭 리스트 맨 위에 빨간 행으로 고정 (발송은 외부 자동체크 경로 사용)
        const disp = p.isExtDispatch ? (p.externalAgencies[0] || null) : null;
        const dispatchedRow = disp ? {
            isDispatched: true, isSoojoo: true,         // isSoojoo → 빨간 강조 스타일 재사용
            no: 0, extId: disp.id,
            seq: '', equipment: disp.equipment || '', pushTime: '',
            name: disp.company || '', phone: disp.phone || '',
            lastAccess: null, lastAccessDisp: '',
            prevOrders: '', monthOrders: '', group: '',
            distance: disp.distance ?? 0,
            level: '', levelNum: 0, levelDesc: '외부배차 지정',
            ownerNum: '', restrictions: {}, smsResult: disp.smsResult || '',
        } : null;
        // 외부배차: 조회=매칭 리스트+상단 지정자, 발송=지정자 1명만 노출(내부 매칭 숨김)
        const rows = dispatchedRow
            ? (state.sendMode ? [dispatchedRow] : [dispatchedRow, ...sorted])
            : sorted;

        const sendKey = state.sendMode ? '1' : '0';
        // ⚠ DataTables init 후엔 <table> 이 .dataTables_wrapper 로 감싸지므로 직계자식(>) 셀렉터 금지
        const $existing = $(`#tA table[data-mlt-tab="internal"][data-mlt-send="${sendKey}"]`).first();
        if ($existing.length && $.fn.DataTable && $.fn.DataTable.isDataTable($existing)) {
            const dt = $existing.DataTable();
            dt.clear().rows.add(rows).draw(false);
            $('#iSA').prop('checked', allChecked);
            $('#sA').html(`최종접속 ${sortArrow}`);  // 정렬화살표 라벨 갱신
            return;
        }

        // 헤더만 빌드 — 행은 DataTables columns 렌더러가 처리
        // 외부배차 오더는 내부 선택이 불필요(지정자 1명 자동발송)하므로 체크박스 컬럼 숨김
        const showCheckbox = state.sendMode && !p.isExtDispatch;
        const checkboxTh = showCheckbox
            ? `<th style="width:36px"><input type="checkbox" id="iSA"${allChecked ? ' checked' : ''}></th>`
            : '';
        $('#tA').html(`
            <table data-mlt-tab="internal" data-mlt-send="${sendKey}"><thead><tr class="hi">
                ${checkboxTh}
                <th style="width:30px">No</th>
                <th>기종/범위</th>
                <th>시간</th>
                <th>이름</th>
                <th>연락처</th>
                <th class="sh" id="sA">최종접속 ${sortArrow}</th>
                <th>전달수주</th>
                <th>이달수주</th>
                <th>그룹</th>
                <th>거리</th>
                <th>차수</th>
                <th style="min-width:200px">차수설명</th>
            </tr></thead></table>`);

        const columns = [];
        if (showCheckbox) {
            columns.push({
                data: null, orderable: false, className: 'text-center',
                render: m => `<input type="checkbox" class="ic2" data-no="${m.no}"${state.checkedInternal.has(m.no) ? ' checked' : ''}>`,
            });
        }
        columns.push(
            { data: 'seq',            className: 'text-center' },
            { data: 'equipment', render: d => Util.escapeHtml(d) },
            { data: 'pushTime' },
            { data: null,             render: m => this._internalNameCell(m) },
            { data: 'phone', render: d => Util.escapeHtml(d) },
            { data: 'lastAccessDisp',
                // 정렬은 timestamp 숫자로, 표시는 색상 HTML로 (attr-order 플러그인 미등록 → orthogonal data)
                // 15일 초과 미접속이면 회색, 그 외 검정 (수주행은 흰색 유지)
                render: (d, type, m) => type !== 'display'
                    ? lastAccessSortKey(m.lastAccess)
                    : `<span style="color:${m.isSoojoo ? '#fff' : (daysSince(m.lastAccess) > 15 ? 'gray' : 'black')}">${d || ''}</span>` },
            { data: 'prevOrders',     className: 'text-center' },
            { data: 'monthOrders',    className: 'text-center' },
            { data: 'group',          className: 'text-center' },
            { data: 'distance',       className: 'text-right',
                // 정렬은 숫자(d)로, 표시는 색상 HTML로 — render 가 type 무시하면 "1km"<"119km" 문자열정렬됨
                render: (d, type, m) => type === 'display'
                    ? `<span style="font-weight:600;color:${m.isSoojoo ? '#fff' : distanceColor(d)}">${d} km</span>`
                    : d },
            { data: 'level',          className: 'text-center',
                // 정렬은 levelNum 숫자로, 표시는 라벨 HTML로
                render: (d, type, m) => type !== 'display'
                    ? m.levelNum
                    : `<span style="font-weight:600">${d}</span>` },
            { data: 'levelDesc',      render: (d, _, m) => {
                const pt = (m.msixPt != null)
                    ? ` <span style="color:#15803D;font-weight:600">(${Number(m.msixPt).toLocaleString()} pt)</span>`
                    : '';
                return `<span style="font-size:12px">${d || ''}${pt}</span>`;
            } }
        );

        const opts = {
            data: rows,
            columns,
            rowCallback: (row, m) => this._styleInternalRow(row, m),
        };
        if (dispatchedRow) opts.order = [];  // 지정 행이 정렬로 밀리지 않게 입력순(맨 위) 유지
        initDataTable($('#tA > table').first(), opts);
    }

    // 이름 셀: 회원링크 + 수주뱃지 + 수발주제한 뱃지 + 발송결과 뱃지
    _internalNameCell(m) {
        // 외부배차 지정자(합성 행) — 회원 링크 없이 상호 + '외부' 뱃지 + 발송결과 뱃지
        if (m.isDispatched) {
            let html = `<span style="font-weight:700;color:#fff">${Util.escapeHtml(m.name)}</span>`
                     + '<span style="margin-left:4px;padding:1px 6px;border-radius:4px;background:#fff;color:#FF7E7E;font-size:11px;font-weight:700">외부</span>';
            const sent = m.smsResult || '';
            if (sent) {
                const cls = sent === '성공' ? 'badge-ok' : (sent === '실패' ? 'badge-fail' : 'badge-pending');
                html += `<span class="badge-sent">발송</span><span class="${cls}">${sent}</span>`;
            }
            return html;
        }
        const soojoo = !!m.isSoojoo;
        const nameColor = soojoo ? '#fff' : '#1D4ED8';
        const fw = soojoo ? 700 : 400;
        const soojooTag = soojoo
            ? '<span style="margin-left:4px;padding:1px 6px;border-radius:4px;background:#fff;color:#FF7E7E;font-size:11px;font-weight:700">수주</span>'
            : '';
        // g5_member_relation: 발주자→수주자 차단(수주제한) / 수주자→발주자 차단(발주제한)
        const restr = m.restrictions || {};
        let restrTag = '';
        if (restr.takeBlocked) {
            restrTag += '<span class="badge-restrict-take" title="발주자가 이 수주자를 차단">수주제한</span>';
        }
        if (restr.giveBlocked) {
            restrTag += '<span class="badge-restrict-give" title="수주자가 이 발주자를 차단">발주제한</span>';
        }
        const sent = m.smsResult || '';
        let sentTag = '';
        if (sent) {
            const cls = sent === '성공' ? 'badge-ok' : (sent === '실패' ? 'badge-fail' : 'badge-pending');
            sentTag = `<span class="badge-sent">발송</span><span class="${cls}">${sent}</span>`;
        }
        // 이름 (소속자는 소유자(소속자)) — 회원 팝오버. 행 클릭과 겹치지 않게 stop
        return `<span style="color:${nameColor};font-weight:${fw}">${$j.F.Admin.Member.Pop.cell(m.no, m.displayName || m.name, { stop: true, name: true })}</span>`
             + soojooTag + restrTag + sentTag;
    }

    // 행 스타일 — 배경(수주/선택/줄무늬) + 차수별 셀색
    _styleInternalRow(row, m) {
        const { state } = this.parent;
        // 외부배차 지정자 행 — 빨간 고정(수주자 스타일). 선택/차수색·행토글(data-mn) 미적용
        if (m.isDispatched) {
            $(row).css('background', '#FF7E7E').find('td').css('color', '#fff');
            return;
        }
        const $row = $(row).attr('data-mn', m.no);
        const $tds = $row.find('td');
        const checked = state.checkedInternal.has(m.no);
        const soojoo  = !!m.isSoojoo;
        const idx     = $row.index();

        // 배경: 수주(빨강) > 선택(파랑) > 줄무늬
        if (soojoo)                         $row.css('background', '#FF7E7E');
        else if (state.sendMode && checked) $row.css('background', '#EFF6FF');
        else if (idx % 2)                   $row.css('background', '#fafafa');

        // 수주자 행은 글자 흰색
        if (soojoo) $tds.css('color', '#fff');

        // 차수별 셀 색 (차수/차수설명 컬럼) — 수주자 행은 빨강이 우선이라 미적용
        if (!soojoo && m.levelNum > 0) {
            const bg = LEVEL_COLORS[m.levelNum % LEVEL_COLORS.length];
            const offset = state.sendMode ? 1 : 0;  // 체크박스 컬럼 보정
            $tds.eq(offset + 10).css('background', bg);  // 차수
            $tds.eq(offset + 11).css('background', bg);  // 차수설명
        }
    }

    // ============================================================
    //  외부배차 테이블 — 발주 car_type 매칭 업체만, 수신거부 회색 처리
    // ============================================================
    renderExternal() {
        const p = this.parent;
        const { state } = p;
        const list = p.externalListFiltered();
        const sendable = p.externalListSendable();
        const allChecked = state.checkedExternal.size === sendable.length && sendable.length > 0;
        const canAssign = !!(p.DATA.order && p.DATA.order.canAssign);   // [지정] 칸 — 서버 판정

        // 동일 탭 내 재렌더(상단버튼 토글 등): 기존 DataTable 을 재사용해 데이터만 교체.
        // clear+add+draw 로 셀이 강제 재렌더되어 체크박스 HTML 도 최신 state 반영.
        // DataTable 인스턴스를 유지하므로 사용자 헤더정렬·페이지 상태는 자동 보존.
        // sendMode 토글 시에는 체크박스 컬럼 수가 바뀌므로 마커 미스매치 → 아래 rebuild 경로로 빠짐.
        // ⚠ DataTables init 후엔 <table> 이 .dataTables_wrapper 로 감싸지므로 직계자식(>) 셀렉터 금지
        const sendKey = state.sendMode ? '1' : '0';
        const $existing = $(`#tA table[data-mlt-tab="external"][data-mlt-send="${sendKey}"]`).first();
        if ($existing.length && $.fn.DataTable && $.fn.DataTable.isDataTable($existing)) {
            const dt = $existing.DataTable();
            dt.clear().rows.add(list).draw(false);  // draw(false) = 페이지 유지
            $('#eSA').prop('checked', allChecked);  // 헤더 전체선택 체크박스 동기화
            return;
        }

        // 체크박스 컬럼은 발송모드(문자발송 클릭)일 때만 노출 — 내부매칭 테이블과 동일
        const checkboxTh = state.sendMode
            ? `<th style="width:36px"><input type="checkbox" id="eSA"${allChecked ? ' checked' : ''}></th>`
            : '';
        // 최초 렌더(또는 탭 전환 직후) — 헤더 HTML + DataTable 신규 생성
        $('#tA').html(`
            <table data-mlt-tab="external" data-mlt-send="${sendKey}"><thead><tr class="he2">
                ${checkboxTh}
                <th style="width:30px">No</th>
                <th>기종/범위</th>
                <th style="width:80px">장비옵션</th>
                <th style="min-width:56px">상호</th>
                <th>연락처</th>
                <th>최근오더일</th>
                <th style="min-width:150px">주소</th>
                <th>거리</th>
                <th>알림동의</th>
                <th style="width:60px">설정</th>
                ${canAssign ? '<th style="width:60px">배차</th>' : ''}
            </tr></thead></table>`);

        const columns = [];
        if (state.sendMode) {
            columns.push({ data: null, orderable: false, className: 'text-center',
                render: e => e.notifyOk
                    ? `<input type="checkbox" class="ec2" data-id="${e.id}"${state.checkedExternal.has(e.id) ? ' checked' : ''}>`
                    : '<span style="font-size:14px;color:#bbb">—</span>' });
        }
        columns.push(
                { data: null, render: (_, __, ___, meta) => `<span style="color:#909090">${meta.row + 1}</span>` },
                { data: 'equipment', render: d => Util.escapeHtml(d) },
                { data: 'options', orderable: false,
                    render: d => d ? d.split(', ').map(o => `<span class="oc">${Util.escapeHtml(o)}</span>`).join('') : '' },
                { data: 'company',
                    render: (d, _, e) => {
                        const sent = e.smsResult || '';
                        if (!sent) return `<span style="font-weight:600">${Util.escapeHtml(d)}</span>`;
                        const cls = sent === '성공' ? 'badge-ok' : (sent === '실패' ? 'badge-fail' : 'badge-pending');
                        return `<span style="font-weight:600">${Util.escapeHtml(d)}</span><span class="badge-sent">발송</span><span class="${cls}">${sent}</span>`;
                    } },
                { data: 'phone', render: d => Util.escapeHtml(d) },
                { data: 'workDay', className: 'text-center',
                    render: d => {
                        if (!d) return '<span style="color:#bbb">—</span>';
                        const isThisYear = d.slice(0, 4) === String(new Date().getFullYear());
                        return `<span style="color:${isThisYear ? '#000' : '#999'}">${d}</span>`;
                    } },
                { data: 'region',
                    render: d => `<span style="color:#5B21B6">${Util.escapeHtml(d)}</span>` },
                { data: 'distance', className: 'text-right',
                    render: (d, type) => type === 'display'
                        ? `<span style="font-weight:600;color:${distanceColor(d)}">${d}km</span>`
                        : d },
                { data: 'agree', className: 'text-center',
                    render: a => {
                        // 0=미확인, 1=동의, 2=동의안함 (외부회원 페이지와 동일)
                        const badge = (bg, fg, txt) =>
                            `<span style="display:inline-block;padding:2px 8px;border-radius:var(--adm-radius-chip, 999px);background:${bg};color:${fg};font-size:12px;font-weight:600">${txt}</span>`;
                        if (+a == 1) return badge('#DCFCE7', '#15803D', '동의');
                        if (+a == 2) return badge('#FEE2E2', '#DC2626', '동의안함');
                        return badge('#F1F5F9', '#64748B', '미확인');
                    } },
                // 설정 — 외부회원 편집 버튼 (실제 적재된 외부회원 id>0 만). /adm/outer/members 와 동일한 편집 모달 공유.
                { data: null, orderable: false, className: 'text-center',
                    render: (_, __, e) => e.id
                        ? `<button type="button" class="om-edit-btn" data-id="${e.id}" style="height:24px;padding:0 10px;border-radius:6px;border:1px solid #CBD5E1;background:#fff;color:#475569;font-size:12px;font-weight:600;cursor:pointer">편집</button>`
                        : '<span style="font-size:14px;color:#bbb">—</span>' },
        );
        // 지정 — 평상시·문자발송 모드 모두. 누르면 그 업체로 외부배차 (MatchList.assignExternal)
        // 외부배차가 안 되는 오더(수주된 적 있음·이미 외부배차)면 칸째로 뺀다 — 판정은 서버(order.canAssign)
        if (canAssign) columns.push({ data: null, orderable: false, className: 'text-center',
            render: (_, __, e) => e.id
                ? `<button type="button" class="ext-assign-btn" data-id="${e.id}" style="height:24px;padding:0 12px;border-radius:6px;border:1px solid #EA580C;background:#EA580C;color:#fff;font-size:12px;font-weight:700;cursor:pointer">지정</button>`
                : '' });

        initDataTable($('#tA > table').first(), {
            data: list,
            columns,
            rowCallback: (row, e, idx) => this._styleExternalRow(row, e, idx, list),
            // 거리 오름차순. 체크박스 컬럼 유무로 거리 컬럼 인덱스가 8(발송모드)/7(평상시) 로 달라짐.
            order: [[ state.sendMode ? 8 : 7, 'asc' ]],
        });

        // 알림동의 칸의 '편집' 버튼 → 외부회원 편집 모달(공유). #tA 는 재렌더에도 유지되므로 위임 1회 바인딩.
        $('#tA').off('click.omedit').on('click.omedit', '.om-edit-btn', (ev) => {
            ev.stopPropagation();
            const id = +$(ev.currentTarget).data('id');
            if (!id) return;
            $j.F.Admin.OutMember.EditForm.open(id, { skipSuccessToast: true, onSaved: () => this._onOutMemberSaved() });
        });
    }

    // 외부회원 편집 저장 후 — 외부배차(external) 탭 선택 상태로 전체 새로고침.
    // (배차 외부회원 정보를 서버에서 재조회 — 연락처가 바뀌면 매칭 키도 달라지므로 로컬 패치보다 안전)
    _onOutMemberSaved() {
        // 편집 버튼은 일반오더 외부탭(배차 전 후보)에만 노출 → 새로고침 후 '문자발송 + 외부배차(external) 탭'으로 재진입.
        $j.Common.Store.session.set('matchList.activeTab', 'external');
        $j.Common.Store.session.set('matchList.sendMode', '1');
        // 확인(OK)을 눌러야 새로고침 — 외부회원 정보를 서버에서 재조회
        $j.Ui.Dialog.open({ tone: 'success', title: '저장완료', desc: '외부회원 정보를 새로고침합니다.', confirm: '확인' })
            .then(() => location.reload());
    }

    // 외부배차 행 스타일 — 수신거부/선택/줄무늬 배경 + 지역 그룹 구분선
    _styleExternalRow(row, e, idx, list) {
        const { state } = this.parent;
        const $row = $(row).attr('data-ei', e.id);
        const checked = state.checkedExternal.has(e.id);

        // 이전 draw 의 스타일이 남아있을 수 있으므로 초기화 (재사용 인스턴스 + 정렬 변경 대비)
        $row.css({ background: '', opacity: '', borderTop: '' });

        // 배경 우선순위: 수신거부 > 선택 > 줄무늬
        if (!e.notifyOk)   $row.css({ background: '#f5f5f5', opacity: .5 });
        else if (checked)  $row.css('background', '#F5F3FF');
        else if (idx % 2)  $row.css('background', '#fafafa');

        // 지역 구분선: 기본 정렬(거리 오름차순)일 때만 표시. 거리 컬럼 인덱스는 체크박스 유무로 8/7.
        // 사용자가 다른 컬럼으로 정렬하면 지역이 흩어져 list[idx-1] 가 display 의 직전 행과 어긋나므로 스킵.
        const distCol = state.sendMode ? 8 : 7;
        const $tbl = $row.closest('table');
        if ($.fn.DataTable && $.fn.DataTable.isDataTable($tbl) && idx > 0) {
            const order = $tbl.DataTable().order();
            const isDefaultSort = order.length === 1 && +order[0][0] === distCol && order[0][1] === 'asc';
            if (isDefaultSort) {
                const prev = list[idx - 1];
                if (prev && e.region !== prev.region) $row.css('border-top', '2px solid #E9E5F5');
            }
        }
    }

    // ============================================================
    //  수주응답 테이블 — 선착순 응답, 배차확정/취소 토글
    // ============================================================
    renderResponse() {
        const list = this.parent.responseList;
        const confirmedIdx = list.findIndex(r => r.status === '배차확정');

        // 상단 헤더 — 응답 개수 + 배차확정 뱃지
        const confirmedBadge = confirmedIdx >= 0
            ? `<span style="padding:2px 10px;border-radius:var(--adm-radius-chip, 999px);background:#FDCF43;color:#79530C;font-size:12px;font-weight:700">배차확정: ${Util.escapeHtml(list[confirmedIdx].name)}</span>`
            : '';
        $('#tA').html(`
            <div style="padding:8px 16px;background:#F0FDF4;border-bottom:1px solid #BBF7D0;font-size:14px;color:#15803D;font-weight:600;display:flex;align-items:center;justify-content:space-between">
                외부배차 수주응답 (선착순) · ${list.length}명${confirmedBadge}
            </div>
            ${list.length ? `<table><thead><tr class="hr">
                <th style="width:50px">순위</th>
                <th>이름/상호</th>
                <th>기종</th>
                <th>거리</th>
                <th>클릭시간</th>
                <th>발송유형</th>
                <th style="width:130px">상태</th>
            </tr></thead></table>` : '<div style="padding:40px;text-align:center;color:#909090">아직 응답이 없습니다.</div>'}`);

        if (!list.length) return;

        initDataTable($('#tA > table').first(), {
            data: list,
            columns: [
                { data: null, className: 'text-center',
                    render: (_, __, ___, meta) => {
                        const cls = ['r1','r2','r3'][meta.row] || 'rn';
                        return `<span class="rank-n2 ${cls}">${meta.row + 1}</span>`;
                    } },
                { data: 'name',        render: d => `<span style="font-weight:600">${Util.escapeHtml(d)}</span>` },
                { data: 'equipment', render: d => Util.escapeHtml(d) },
                { data: 'distance',
                    render: (d, type) => type !== 'display'
                        ? d
                        : d > 0
                            ? `<span style="font-weight:600;color:${distanceColor(d)}">${d}km</span>`
                            : '<span style="font-weight:600;color:#15803D">현장</span>' },
                { data: 'clickTime',   render: d => `<span style="font-weight:700;color:#15803D">${d}</span>` },
                { data: 'messageType', render: d => msgTypeBadge(d) },
                { data: null, orderable: false,
                    render: (_, __, r, meta) => this._responseActionCell(r, meta.row, confirmedIdx) },
            ],
            rowCallback: (row, r, idx) => {
                if (r.status === '배차확정')  $(row).css('background', '#FFFEF0');
                else if (idx % 2)             $(row).css('background', '#fafafa');
            },
        });
    }

    // 응답 액션 셀 — 배차확정/취소/대기
    _responseActionCell(r, idx, confirmedIdx) {
        if (r.status === '배차확정') {
            return `<button class="conf-cancel" data-ri="${idx}" style="padding:3px 14px;border-radius:var(--adm-radius-chip, 999px);border:1px solid #DC2626;background:#FFF5F5;color:#DC2626;font-size:12px;font-weight:700;cursor:pointer;font-family:inherit">배차취소</button>`;
        }
        if (confirmedIdx >= 0) {
            return '<span style="padding:2px 10px;border-radius:var(--adm-radius-chip, 999px);background:#f1f1f1;color:#909090;font-size:12px;font-weight:600">대기</span>';
        }
        return `<button class="conf-btn" data-ri="${idx}" style="padding:3px 14px;border-radius:var(--adm-radius-chip, 999px);border:1px solid #15803D;background:#F0FDF4;color:#15803D;font-size:12px;font-weight:700;cursor:pointer;font-family:inherit">배차확정</button>`;
    }
}